/** @jsxRuntime automatic */
import type { LucideIcon, LucideProps } from "lucide-react";
import { cn } from "./utils";

/** 12 inside dense badges · 16 inline/buttons · 20 nav and list actions · 24 app bar and empty-state tiles. */
const ICON_SIZE = { xs: 12, sm: 16, md: 20, lg: 24 } as const;
export type IconSize = keyof typeof ICON_SIZE | 12 | 16 | 20 | 24;

export interface IconProps extends Omit<LucideProps, "size" | "ref"> {
  /** A lucide-react icon component, e.g. `import { Search } from "lucide-react"`. */
  icon: LucideIcon;
  /** xs 12 (badges) · sm 16 (default) · md 20 · lg 24. */
  size?: IconSize;
  /**
   * Accessible name. Without it the icon is decorative (`aria-hidden`), which is
   * right whenever a visible label or the button's `aria-label` already names it.
   */
  label?: string;
  /**
   * Active/selected state (e.g. the current tab): Lucide has no filled set, so
   * the stroke thickens and the shape gets a 15 % fill. Passed (true or
   * false), the fill is always there at 0 or 15 % and both change in 120
   * (never `fill: none` → a color mid-tween, which flashes solid).
   */
  active?: boolean;
}

/**
 * The only way to draw an icon (spec 07 › Iconografía): one set (Lucide), three
 * sizes, a 1.75 stroke. No inline `<svg>` in screens and no emoji as icons.
 */
export function Icon({ icon: LucideGlyph, size = "sm", label, active, className, ...props }: IconProps) {
  const px = typeof size === "number" ? size : ICON_SIZE[size];
  // A toggle (the tab, the nav, a thumb, the bookmark): stroke and fill tween; an icon that never toggles stays bare.
  const toggles = active !== undefined;
  return (
    <LucideGlyph
      size={px}
      strokeWidth={active ? 2.25 : 1.75}
      fill={toggles ? "currentColor" : "none"}
      fillOpacity={toggles ? (active ? 0.15 : 0) : undefined}
      className={cn("shrink-0", toggles && "transition-[stroke-width,fill-opacity] duration-fast ease-enter", className)}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      {...props}
    />
  );
}

export type { LucideIcon };
